import React, { useLayoutEffect, useRef } from 'react';
import { DEFAULT_IDS_TO_PERSIST, useFocusTrap } from '../hooks/useFocusTrap';
import { getFirstAndLastFocusableElements } from 'dom-utils/lib/utils/tabKeyUtils';
import { shiftTabKeyHandler, tabKeyHandler } from 'window-utils/lib/keyHandlerUtils';
import { trySetFocusOnElement } from 'dom-utils/lib/utils/focusUtils';

export interface FocusTrapStateParams {
    ariaHidden: boolean;
    display: string;
}

interface FocusTrapZoneProps {
    /**
     * The id of the trapzone to be created
     */
    id: string;
    /**
     * Element to focus on open of the element
     */
    focusOnOpen?: React.RefObject<any>;

    /**
     * Element to focus on close of the focus trap zone
     */
    focusOnClose?: React.RefObject<any>;

    /**
     * ids to not be hidden
     */
    idsToPersist?: string[];

    /**
     * If true, the elements outside the focus trap zone will be made invisible
     */
    hideBackground?: boolean;

    /**
     * If true, focus won't go outside the focus trap zone upon tabbing
     */
    restrictTabOrder?: boolean;
}

export const FocusTrapZone: React.FunctionComponent<FocusTrapZoneProps> = (props): JSX.Element => {
    const {
        id,
        children,
        focusOnClose,
        focusOnOpen,
        idsToPersist = DEFAULT_IDS_TO_PERSIST,
        hideBackground = false,
        restrictTabOrder = false,
    } = props;

    const { createFocusTrap, relinquishFocusTrap } = useFocusTrap(id, hideBackground, idsToPersist);

    const handleTabKey = tabKeyHandler(e => e.preventDefault());
    const handleShiftTabKey = shiftTabKeyHandler(e => e.preventDefault());
    const firstFocusableElement = useRef(null);
    const lastFocusableElement = useRef(null);

    function addTabRestrictions(): void {
        firstFocusableElement.current?.addEventListener('keydown', handleShiftTabKey);
        lastFocusableElement.current?.addEventListener('keydown', handleTabKey);
    }

    function removeTabRestrictions(): void {
        firstFocusableElement.current?.removeEventListener('keydown', handleShiftTabKey);
        lastFocusableElement.current?.removeEventListener('keydown', handleTabKey);
    }

    useLayoutEffect(
        () => {
            // Component did mount
            createFocusTrap();

            if (restrictTabOrder) {
                const targetDomElement = document.getElementById(id);
                [
                    firstFocusableElement.current,
                    lastFocusableElement.current,
                ] = getFirstAndLastFocusableElements(targetDomElement);

                setTimeout(() => {
                    addTabRestrictions();
                }, 0);
            }

            focusOnOpen && setTimeout(() => trySetFocusOnElement(focusOnOpen), 0);

            return () => {
                // Component will unmount
                // Using the previous elements
                relinquishFocusTrap();
                focusOnClose && setTimeout(() => trySetFocusOnElement(focusOnClose), 0);
                restrictTabOrder && removeTabRestrictions();
            };
        },
        [
            /** kept empty to ensure this happens only on component mount and unmount */
        ]
    );

    return <>{children}</>;
};
